import { extendTheme } from '@chakra-ui/react';

const theme = extendTheme({
  colors: {
    brand: {
      50: '#FFF5F2',
      100: '#FFE5D9',
      200: '#FFC5B0',
      300: '#FFA587',
      400: '#FF855E',
      500: '#FF6B35', // Primary orange
      600: '#E55A2B',
      700: '#CC4921',
      800: '#B33817',
      900: '#8B1538', // Primary maroon (used as 900)
    },
    maroon: {
      50: '#FDF2F4',
      100: '#F9D6DC',
      200: '#F5BAC4',
      300: '#F19EAC',
      400: '#ED8294',
      500: '#E9667C',
      600: '#D14A64',
      700: '#B92E4C',
      800: '#8B1538', // Primary maroon
      900: '#6B0F2A', // Primary dark
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    success: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#10B981',
      600: '#059669',
      700: '#047857',
      800: '#065F46',
      900: '#064E3B',
    },
    warning: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
    error: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
    },
    info: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
    },
  },
  fonts: {
    heading: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    body: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
  },
  space: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '4xl': '5rem',
  },
  radii: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 600,
        borderRadius: 'md',
        fontFamily: 'body',
        transition: 'all 0.2s ease-in-out',
      },
      variants: {
        primary: {
          // Accessible CTA gradient — darkened orange stop so white text meets
          // WCAG AA 4.5:1 across the whole gradient (see --color-cta-orange).
          bg: 'linear-gradient(135deg, var(--color-cta-orange) 0%, var(--color-primary-maroon) 100%)',
          color: 'white',
          boxShadow: 'md',
          _hover: {
            transform: 'translateY(-1px)',
            boxShadow: 'lg',
            bg: 'linear-gradient(135deg, var(--color-cta-orange-hover) 0%, var(--color-primary-dark) 100%)',
            _disabled: {
              transform: 'none',
              // Chakra's Button baseStyle ships `_hover: { _disabled: { bg:
              // 'initial' } }`, which blanks the background the moment you hover
              // a disabled button. `isLoading` disables the button, so any
              // in-flight action rendered as a WHITE RECTANGLE under the cursor —
              // most visibly on a spinner you're waiting on and therefore
              // pointing at. Restating the resting gradient keeps the button
              // looking like a button while it works.
              bg: 'linear-gradient(135deg, var(--color-cta-orange) 0%, var(--color-primary-maroon) 100%)',
            },
          },
          _active: {
            transform: 'translateY(0)',
            boxShadow: 'md',
          },
          _disabled: {
            opacity: 0.6,
            cursor: 'not-allowed',
            transform: 'none',
          },
        },
        secondary: {
          bg: 'white',
          color: 'maroon.800',
          border: '1px solid',
          borderColor: 'maroon.800',
          _hover: {
            bg: 'maroon.50',
            borderColor: 'maroon.800',
            color: 'maroon.800',
            // Same Chakra baseStyle reset as in `primary` above — without this a
            // loading secondary button goes transparent on hover, which reads as
            // the button vanishing anywhere the surface behind it isn't white.
            _disabled: {
              bg: 'white',
            },
          },
        },
        ghost: {
          bg: 'transparent',
          color: 'gray.700',
          _hover: {
            bg: 'gray.50',
          },
        },
        link: {
          bg: 'transparent',
          color: 'maroon.800',
          textDecoration: 'underline',
          fontWeight: 600,
          _hover: {
            color: 'maroon.900',
            textDecoration: 'underline',
          },
        },
      },
      defaultProps: {
        variant: 'primary',
      },
    },
    Badge: {
      baseStyle: {
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: 'full',
        px: 3,
        py: 1,
        fontSize: 'xs',
      },
      variants: {
        active: {
          bg: 'success.50',
          color: 'success.700',
          border: '1px solid',
          borderColor: 'success.200',
        },
        completed: {
          bg: 'info.50',
          color: 'info.700',
          border: '1px solid',
          borderColor: 'info.200',
        },
        withdrawn: {
          bg: 'error.50',
          color: 'error.700',
          border: '1px solid',
          borderColor: 'error.200',
        },
        pending: {
          bg: 'warning.50',
          color: 'warning.700',
          border: '1px solid',
          borderColor: 'warning.200',
        },
        primary: {
          bg: 'brand.50',
          color: 'brand.700',
          border: '1px solid',
          borderColor: 'brand.200',
        },
        secondary: {
          bg: 'gray.100',
          color: 'gray.700',
          border: '1px solid',
          borderColor: 'gray.300',
        },
        error: {
          bg: 'error.50',
          color: 'error.700',
          border: '1px solid',
          borderColor: 'error.200',
        },
        warning: {
          bg: 'warning.50',
          color: 'warning.800',
          border: '1px solid',
          borderColor: 'warning.200',
        },
        info: {
          bg: 'info.50',
          color: 'info.700',
          border: '1px solid',
          borderColor: 'info.200',
        },
      },
    },
    Alert: {
      baseStyle: {
        container: {
          borderRadius: 'md',
          px: 4,
          py: 3,
        },
        title: {
          fontWeight: 600,
          fontSize: 'sm',
        },
        description: {
          fontSize: 'sm',
        },
      },
      variants: {
        success: {
          container: {
            bg: 'success.50',
            border: '1px solid',
            borderColor: 'success.200',
            color: 'success.800',
          },
        },
        error: {
          container: {
            bg: 'error.50',
            border: '1px solid',
            borderColor: 'error.200',
            color: 'error.800',
          },
        },
        warning: {
          container: {
            bg: 'warning.50',
            border: '1px solid',
            borderColor: 'warning.200',
            color: 'warning.800',
          },
        },
        info: {
          container: {
            bg: 'info.50',
            border: '1px solid',
            borderColor: 'info.200',
            color: 'info.800',
          },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'white',
          borderRadius: 'lg',
          border: '1px solid',
          borderColor: 'gray.200',
          boxShadow: 'sm',
          transition: 'all 0.2s ease-in-out',
        },
        header: {
          px: 6,
          py: 4,
          borderBottom: '1px solid',
          borderColor: 'gray.200',
        },
        body: {
          px: 6,
          py: 4,
        },
        footer: {
          px: 6,
          py: 4,
          borderTop: '1px solid',
          borderColor: 'gray.200',
        },
      },
      variants: {
        elevated: {
          container: {
            boxShadow: 'md',
            _hover: {
              boxShadow: 'lg',
              transform: 'translateY(-2px)',
            },
          },
        },
        outline: {
          container: {
            boxShadow: 'none',
          },
        },
        filled: {
          container: {
            bg: 'gray.50',
            borderColor: 'gray.200',
          },
        },
      },
      defaultProps: {
        variant: 'outline',
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: 'heading',
        fontWeight: 700,
        color: 'gray.900',
      },
      sizes: {
        '4xl': {
          fontSize: '4xl',
          lineHeight: 1.2,
        },
        '3xl': {
          fontSize: '3xl',
          lineHeight: 1.2,
        },
        '2xl': {
          fontSize: '2xl',
          lineHeight: 1.3,
        },
        xl: {
          fontSize: 'xl',
          lineHeight: 1.4,
        },
        lg: {
          fontSize: 'lg',
          lineHeight: 1.5,
        },
        md: {
          fontSize: 'md',
          lineHeight: 1.5,
        },
        sm: {
          fontSize: 'sm',
          lineHeight: 1.5,
        },
      },
    },
    Text: {
      baseStyle: {
        fontFamily: 'body',
        color: 'gray.700',
      },
      variants: {
        muted: {
          color: 'gray.600',
        },
        strong: {
          fontWeight: 600,
          color: 'gray.900',
        },
      },
    },
    Progress: {
      baseStyle: {
        track: {
          borderRadius: 'full',
          bg: 'gray.200',
        },
        filledTrack: {
          borderRadius: 'full',
        },
      },
      variants: {
        primary: {
          filledTrack: {
            bg: 'brand.500',
          },
        },
        success: {
          filledTrack: {
            bg: 'success.500',
          },
        },
        warning: {
          filledTrack: {
            bg: 'warning.500',
          },
        },
        error: {
          filledTrack: {
            bg: 'error.500',
          },
        },
      },
      defaultProps: {
        variant: 'primary',
      },
    },
    Input: {
      baseStyle: {
        field: {
          borderRadius: 'md',
          fontFamily: 'body',
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: 'gray.300',
            _hover: {
              borderColor: 'gray.400',
            },
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
            },
            _invalid: {
              borderColor: 'error.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-error-500)',
            },
          },
        },
      },
      defaultProps: {
        variant: 'outline',
      },
    },
    Select: {
      baseStyle: {
        field: {
          borderRadius: 'md',
          fontFamily: 'body',
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: 'gray.300',
            _hover: {
              borderColor: 'gray.400',
            },
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
            },
            _invalid: {
              borderColor: 'error.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-error-500)',
            },
          },
        },
      },
      defaultProps: {
        variant: 'outline',
      },
    },
    Textarea: {
      baseStyle: {
        borderRadius: 'md',
        fontFamily: 'body',
      },
      variants: {
        outline: {
          borderColor: 'gray.300',
          _hover: {
            borderColor: 'gray.400',
          },
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
          },
          _invalid: {
            borderColor: 'error.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-error-500)',
          },
        },
      },
      defaultProps: {
        variant: 'outline',
      },
    },
    Switch: {
      baseStyle: {
        track: {
          _checked: {
            bg: 'brand.500',
          },
        },
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          borderRadius: 'sm',
          borderColor: 'gray.300',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: {
              bg: 'brand.600',
              borderColor: 'brand.600',
            },
          },
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: 'lg',
        },
        header: {
          fontFamily: 'heading',
          fontWeight: 700,
        },
      },
    },
    Radio: {
      baseStyle: {
        control: {
          borderColor: 'gray.300',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: {
              bg: 'brand.600',
              borderColor: 'brand.600',
            },
          },
        },
      },
    },
    Divider: {
      baseStyle: {
        borderColor: 'gray.200',
      },
    },
  },
  styles: {
    global: {
      body: {
        bg: 'gray.50',
        color: 'gray.900',
        fontFamily: 'body',
      },
    },
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
});

export default theme;

